import { MainService } from 'rainbow-web-sdk/services/core/main.service';
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { Service } from "rainbow-web-sdk/services/service";
import { XmppService } from 'rainbow-web-sdk/services/core/xmpp.service';
import { RBEvent } from 'rainbow-web-sdk/models/event.model';
import { DEVICE_MANAGEMENT_SVC, DeviceManagementServiceRB } from '../deviceManagement/deviceManagement.service';
import { WEBRTC_CONFIG_UTILS_SVC, WebrtcConfigAndUtils } from '../webrtc/webrtcConfigAndUtils';
import { SettingsService } from 'rainbow-web-sdk/services/core/settings.service';
import { ContactService } from '../core/contact.service';

export const CITRIX_SVC = 'CitrixService';

export class CitrixService extends Service {
    private logger: LoggerService = LoggerService.getInstance();
    private mainService: MainService = MainService.getInstance();
    private xmppService: XmppService = XmppService.getInstance();
    private settingsService: SettingsService = SettingsService.getInstance();
    private contactService: ContactService = ContactService.getInstance();
    private deviceManagementService: DeviceManagementServiceRB;
    private webrtcConfigAndUtils: WebrtcConfigAndUtils;
    private centralizedService;

    public started: boolean = false;
    public vdiClientConnected: boolean = false;
    public citrixSDK;
    private apiOverwrited: boolean = false;

    private nativeAPIs = {};

    private vendorName = "ALE INTERNATIONAL";

    public static getInstance() {
        return Service.create(CITRIX_SVC, CitrixService);
    }

    public static build() {
        return new CitrixService();
    }

    protected constructor() {
        super();
    }

    public async start() {
        if (this.xmppService.connected) { this.initCitrixSDK(); }
        this.xmppService.subscribeToConnectionEvents((event: RBEvent) => { this.onConnectionStateChangeEvent(event); });
    }

    public stop() {
        if (this.started) {
            this.logger.log('[CitrixService] stop');
            this.resetGlobalAPIs();
            this.started = false;
            this.vdiClientConnected = false;
            this.citrixSDK?.deinitUCSDK();
        }
    }

    private initCitrixSDK() {
        if (this.started) return;
        //check if user have right to use video before activating citrix

        this.started = true;
        this.logger.log('[CitrixService] start -- initCitrixSDK');

        // if (window["CitrixBootstrap"]) {
        //     window["CitrixBootstrap"].initBootstrap(this.vendorName);
        // }

        this.citrixSDK = window["CitrixWebRTC"];
        if (this.citrixSDK) {
            this.citrixSDK.initLog(this.logger);
            this.setWindowAPIs();

            this.citrixSDK.setVMEventCallback((event) => {
                try {
                    //1-9: applications should handle this VM event properly
                    this.logger.info('[CitrixService] setVMEventCallback notification received with event: ' + (event?.event) ? event.event : event);
                    if (event['event'] === 'vdiClientConnected') {
                        this.overwriteGlobalAPIs();
                        this.vdiClientConnected = true;
                        this.updatePopoutConfiguration();
                        //1-10: Citrix VM session with webrtc redirection has been established
                        // event['type_script'] indicates SDK version
                        // event['webrpc'] indicates redirection engine version on client endpoint
                        // event['webrtc_codecs'] indicates webrtc native base version, for example, M76
                        // event['receiver'] indicates Citrix CWA version

                        this.citrixSDK.setCodecCapabilities({ codecs: [{ name: "VP8", enabled: true }] });
                    } else if (event['event'] === 'vdiClientDisconnected') {
                        this.vdiClientConnected = false;
                        //should reforce reconnection in case we've the network UP
                        //1-11: Citrix VM session with webrtc redirection has been disconnected
                        // perform proper handling here, perhaps fall back to native webrtc implementation?
                    } else if (event['event'] === 'vdiUnableToRemote') {
                        //1-12: Citrix VM session failed to redirect webrtc
                        // (note: this is deprecated event in the later release with other alternatives)
                        // perform proper error handling here
                    }
                }
                catch (error) {
                    this.logger.warn(`[CitrixService] setVMEventCallback error -- ${error}`);
                }
            });

            this.citrixSDK.initUCSDK(this.vendorName);
        }
    }

    private setWindowAPIs() {
        this.logger.log('[CitrixService] start -- setWindowAPIs');
        this.centralizedService = this.mainService.inject("CentralizedService");
        window["getCitrixWebrtcRedir"] = this.centralizedService.system.citrix.getCitrixWebrtcRedir;
        window["getWindowHandleAsHex"] = this.centralizedService.system.citrix.getWindowHandleAsHex;
    }

    private overwriteGlobalAPIs() {
        if (this.apiOverwrited) {
            this.logger.log('[CitrixService] overwriteGlobalAPIs already set'); return;
        }

        this.logger.log('[CitrixService] start -- overwriteGlobalAPIs');
        this.apiOverwrited = true;
        this.deviceManagementService = this.mainService.inject(DEVICE_MANAGEMENT_SVC);
        this.webrtcConfigAndUtils = this.mainService.inject(WEBRTC_CONFIG_UTILS_SVC);

        navigator.mediaDevices["enumerateDevices2"] = navigator.mediaDevices.enumerateDevices;
        navigator.mediaDevices.enumerateDevices = this.citrixSDK.enumerateDevices;

        //overwrite the set sink id API
        this.nativeAPIs["setSpeakerForAudioElement"] = this.deviceManagementService.setSpeakerForAudioElement;

        //map the global audio tag
        this.citrixSDK.mapAudioElement(document.getElementById("globalAudioTag"));

        this.deviceManagementService.setSpeakerForAudioElement = async (element, device) => {
            //find the same device from the original list of devices and select it
            const devices = await navigator.mediaDevices["enumerateDevices2"]();
            let deviceToUse;
            let backupDeviceToUse;
            let labelToFind;
            if (device.label.indexOf("(") !== -1) {
                labelToFind = device.label.substring(device.label.indexOf("(") + 1, device.label.lastIndexOf(")"));
            }

            devices.forEach((dev) => {
                if (dev.kind === device.kind) {
                    if (device.label.indexOf(dev.label) !== -1 || dev.label.indexOf(device.label) !== -1) {
                        deviceToUse = dev;
                    }
                    if (labelToFind && dev.label.indexOf(labelToFind) !== -1) {
                        backupDeviceToUse = dev;
                    }
                }
            });

            if (!deviceToUse && backupDeviceToUse) {
                //we could not find device, try to find via smaller label only
                deviceToUse = backupDeviceToUse;
            }

            await this.nativeAPIs["setSpeakerForAudioElement"].bind(this)(element, deviceToUse);
        };

        this.nativeAPIs["getUserMedia"] = navigator.mediaDevices.getUserMedia;
        navigator.mediaDevices.getUserMedia = async (constraints: any) => {
            //if no video is asked, delete the whole line for video

            //manage sharing
            if (constraints.video?.mandatory?.chromeMediaSourceId) {
                const screenInfos = await this.citrixSDK.getScreensAsync();
                let index = 0;
                if (screenInfos.length > 1) {
                    //try to find the right screen but ...
                    if (constraints.video?.mandatory?.chromeMediaSourceId.indexOf("0:0") === -1) {
                        index = 1;
                    }
                }
                await this.citrixSDK.setScreenSharePanelId(screenInfos[index].id);
                constraints = {
                    video: {
                        width: { max: 1920 },
                        height: { max: 1080 },
                        frameRate: 10
                    }
                };
                this.logger.log(`[CitrixService] getDisplayMedia -- ${JSON.stringify(constraints)}`);
                return this.citrixSDK.getDisplayMedia(constraints);
            }

            // let constrainsVideo;

            if (!constraints.video?.width) {
                delete constraints.video;
            }

            // delete constraints.video;

            // //in case of audio and video, we need to get them separately (thanks citrix); And combine them in a single stream that is returned
            // //from the API as otherwise I need to manage it completely differently in the whole app;
            // if (constrainsVideo) {
            //     this.logger.log(`[CitrixService] getUserMedia for audio -- ${JSON.stringify(constraints)}`);
            //     const streamAudio: MediaStream = await this.citrixSDK.getUserMedia(constraints);

            //     this.logger.log(`[CitrixService] getUserMedia for video -- ${JSON.stringify(constrainsVideo)}`);
            //     const streamVideo: MediaStream = await this.citrixSDK.getUserMedia({video: constrainsVideo});

            //     const tracks = streamAudio.getTracks().concat(streamVideo.getTracks());
            //     this.logger.log(`[CitrixService] getUserMedia combine tracks -- ${tracks?.length}`);
            //     return new MediaStream(tracks);
            // }

            //audio only
            this.logger.log(`[CitrixService] getUserMedia -- ${JSON.stringify(constraints)}`);
            return this.citrixSDK.getUserMedia(constraints);

        };

        this.deviceManagementService.checkDeviceList();

        this.nativeAPIs["RTCPeerConnection"] = window.RTCPeerConnection;
        window.RTCPeerConnection = this.citrixSDK.CitrixPeerConnection;

        this.nativeAPIs["attachMediaStream"] = this.webrtcConfigAndUtils.RTC.attachMediaStream;
        this.nativeAPIs["clearMediaStream"] = this.webrtcConfigAndUtils.RTC.clearMediaStream;
        this.webrtcConfigAndUtils.RTC.attachMediaStream = (element, stream) => {
            if (element[0]?.nodeName === "VIDEO") {
                // this.windowRef = await window["getWindowReference"]();
                this.citrixSDK.mapVideoElement(element[0]);
            }
            else { this.citrixSDK.mapAudioElement(element[0]); }
            this.nativeAPIs["attachMediaStream"](element, stream);
            element[0].play();
        };
        this.webrtcConfigAndUtils.RTC.clearMediaStream = (element) => { this.nativeAPIs["clearMediaStream"](element); };

        this.settingsService.setSetting("citrixConnected", "true");
        this.xmppService.connection.jingle.ice_config["citrix"] = true;

        //enable DTLS SRTP for citrix env
        this.xmppService.connection.jingle.ice_config["enableDtlsSrtp"] = true;

        this.xmppService.connection.jingle.media_constraints = {
            mandatory: {
                OfferToReceiveAudio: true,
                OfferToReceiveVideo: true
            },
            OfferToReceiveAudio: true,
            OfferToReceiveVideo: true
        };
    }

    private resetGlobalAPIs(disconnected: boolean = false) {
        this.logger.log('[CitrixService] stop -- resetGlobalAPIs');
        this.deviceManagementService = this.mainService.inject(DEVICE_MANAGEMENT_SVC); ;
        this.webrtcConfigAndUtils = this.mainService.inject(WEBRTC_CONFIG_UTILS_SVC);

        this.apiOverwrited = false;

        navigator.mediaDevices.getUserMedia = this.nativeAPIs["getUserMedia"];
        navigator.mediaDevices.enumerateDevices = navigator.mediaDevices["enumerateDevices2"];
        this.deviceManagementService.setSpeakerForAudioElement = this.nativeAPIs["setSpeakerForAudioElement"];

        window.RTCPeerConnection = this.nativeAPIs["RTCPeerConnection"];
        this.webrtcConfigAndUtils.RTC.clearMediaStream = this.nativeAPIs["clearMediaStream"];
        this.webrtcConfigAndUtils.RTC.attachMediaStream = this.nativeAPIs["attachMediaStream"];

        this.deviceManagementService.setSpeakerForAudioElement = this.nativeAPIs["setSpeakerForAudioElement"];

        this.settingsService.setSetting("citrixConnected", "false");
        this.xmppService.connection.jingle.ice_config["citrix"] = false;

        //enable DTLS SRTP for citrix env
        this.xmppService.connection.jingle.ice_config["enableDtlsSrtp"] = false;
        this.xmppService.connection.jingle.media_constraints = {};

        if (disconnected) {
            //reset the list of devices
            this.deviceManagementService.checkDeviceList();
        }
    }

    private async onConnectionStateChangeEvent(event: RBEvent) {
        if (event.name === 'ON_XMPP_DISCONNECTED') {
            if (event.data.reason === "LOGOUT") {
                this.stop();
            }
            else {
                this.logger.log('[CitrixService] onConnectionStateChangeEvent : disconnected');
            }
        }
        else if (event.name === 'ON_XMPP_CONNECTED') {
            this.logger.log('[CitrixService] onConnectionStateChangeEvent : connected');
            if (!this.started) {
                await this.initCitrixSDK();
            }

            this.updatePopoutConfiguration();
        }
    }

    private updatePopoutConfiguration() {
        if (this.vdiClientConnected) {
            //check if we need to disable pop-out
            if (this.contactService?.userContact?.webrtcVideoEnabled || this.contactService?.userContact?.webrtcSharingEnabled) {
                //disable pop-out in case of VDI connected
                this.settingsService.setSetting("openPopoutOnMinimize", "false");
                this.settingsService.setSetting("openPopoutOnAnswerCall", "false");
                this.settingsService.setSetting("endCallOnClosePopout", "false");
            }
        }
    }
}
